import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type ActiveScopeEntry,
	type DismissCallback,
	type PostOfficeContextValue,
	type RouteMatch,
	type RouteState,
	createMatchScopes,
	useContextScope,
	useMessagePlacementHooks,
} from '@atlassian/post-office-context';
import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';
import {
	type ErrorBoundariedProps,
	MaybeSuspenseBoundary,
	createPlacementComponent,
	useInitialData,
} from '@post-office/placement-common';
import { usePlacementApi } from '@post-office/use-placement-api';
import { type ComponentType, memo, useCallback, useEffect, useState } from 'react';

import { scopes } from './scopes';
import { MessageRenderer } from '../__generated__/client/render';
import { PLACEMENT_ID } from '../__generated__/common';
import { withScreenSpaceFlagsIntlProvider } from '../__generated__/intl-provider';
import { type ScreenSpaceFlagsMessageState } from '../api/types';
import type { Placement as ScreenSpaceFlagsPlacement } from '../contracts/Placement';

export const matchedScopes = (input: {
	route: RouteState | RouteMatch;
	context: PostOfficeContextValue;
}): ActiveScopeEntry[] => createMatchScopes({ scopes })(input);

export const ScreenSpaceFlagsPlacementComponent: ComponentType<
	ErrorBoundariedProps<ScreenSpaceFlagsPlacement>
> = withScreenSpaceFlagsIntlProvider(
	createPlacementComponent<ScreenSpaceFlagsPlacement>(
		PLACEMENT_ID,
		memo(({ initialData, preloadedInitialData, setPreloadedInitialData, jiraFlagsService }) => {
			const [localMessageState, setLocalMessageState] = useState<
				ScreenSpaceFlagsMessageState[] | undefined
			>(() => {
				if (preloadedInitialData) {
					return preloadedInitialData?.messages?.length > 0
						? preloadedInitialData?.messages
						: undefined;
				}

				return undefined;
			});

			useInitialData(initialData);

			const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

			const handleDismiss = useCallback<DismissCallback>(
				({ messageInstanceId }: { messageInstanceId: string }) => {
					setLocalMessageState((current) => {
						if (current) {
							const newState = current.filter((e) => e.messageInstanceId !== messageInstanceId);

							if (typeof setPreloadedInitialData === 'function') {
								setPreloadedInitialData({
									messages: newState,
								});
							}

							return newState;
						}

						return undefined;
					});
				},
				[setPreloadedInitialData],
			);

			const onDismissed = useCallback(() => {
				setLocalMessageState([]);

				if (typeof setPreloadedInitialData === 'function') {
					setPreloadedInitialData({
						messages: [],
					});
				}
			}, [setPreloadedInitialData]);

			useEffect(() => onMessageDismiss(handleDismiss), [onMessageDismiss]);
			useEffect(() => onMessageClicked(handleDismiss), [onMessageClicked]);
			useEffect(() => onMessageSnooze(handleDismiss), [onMessageSnooze]);

			const activeScopes = useContextScope({ scopes });

			const messagesState = usePlacementApi<{
				messages: ScreenSpaceFlagsMessageState[];
			}>('/update-messages', {
				requestOptions: { method: 'POST', body: JSON.stringify(activeScopes) },
				throttleTime: 100,
				autoRefetch: {
					isEnabled: !!activeScopes.updateKey && typeof localMessageState === 'undefined',
					updateKey: activeScopes.updateKey ?? '',
				},
			});

			useEffect(() => {
				if (messagesState?.data?.messages?.length) {
					setLocalMessageState(messagesState.data.messages);
				}
			}, [messagesState.data]);

			return FeatureGates.checkGate('post-office-screen-space-flags-shadow') ? (
				<SuppressIfModalIsPresent>
					{localMessageState?.slice(0, 1).map(({ messageInstanceId, ...props }) => (
						// Adding @ts-ingore to suppress the error in confluence typecheck and below error is not thrown in post-office typecheck
						// This error exists because confluence is using react 16 and post-office is using react 18
						// eslint-disable-next-line @typescript-eslint/ban-ts-comment
						// @ts-ignore - 'ReactNode | ReactElement<any, any>' is not a valid JSX element.
						<MessageRenderer
							{...props}
							key={messageInstanceId}
							id={messageInstanceId}
							messageInstanceId={messageInstanceId}
							onDismissed={onDismissed}
							SuspenseBoundary={MaybeSuspenseBoundary}
							jiraFlagsService={jiraFlagsService}
						/>
					))}
				</SuppressIfModalIsPresent>
			) : null;
		}),
		{
			choreographer: {
				isDefaultEnabled: true, // this is the default value, but leaving it as explicitly defined
			},
		},
	),
);

export default ScreenSpaceFlagsPlacementComponent;

const SuppressIfModalIsPresent = ({ children }: React.PropsWithChildren<unknown>): JSX.Element => {
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const [isVisible, setIsVisible] = useState(true);

	const handleVisibilityChange = useCallback(
		(modalOnScreen: boolean) => {
			const shouldShow = !modalOnScreen;

			if (!shouldShow) {
				fireAnalyticsEvent({
					eventType: 'operational',
					action: 'suppressed',
					actionSubject: 'screenSpaceFlags',
				});
			}

			setIsVisible(shouldShow);
		},
		[fireAnalyticsEvent],
	);

	useEffect(() => {
		const modalOnScreen = !!globalThis.document.querySelector(
			'.atlaskit-portal-container .atlaskit-portal [role=dialog]',
		);

		handleVisibilityChange(modalOnScreen);
	}, [handleVisibilityChange]);

	return FeatureGates.checkGate('post-office-screen-space-flags-modal-detector') ? (
		<>{isVisible && children}</>
	) : (
		<>{children}</>
	);
};
